import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Clock, CalendarCheck, Heart, MessageCircle, ShieldCheck, Instagram } from "lucide-react";
import { CONTACT_INFO, SERVICES, WHATSAPP, INSTAGRAM_URL, MAPS_LINK } from "../data/siteData";
import { trackEvent } from "../lib/analytics";

export const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-navy text-white mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="mb-4">
              <span className="inline-flex bg-white rounded-2xl px-4 py-2.5">
                <img src="/thrive-logo.png" alt="Thrive Pediatric Therapy" className="h-11 w-auto" />
              </span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              Physiotherapy, occupational therapy, speech & language therapy and feeding therapy for children.
            </p>
            <p data-testid="footer-legal-name" className="mt-3 text-white/40 text-xs leading-relaxed">
              Thrive Pediatric Therapy is a trading name of Thrive Physiotherapy.
            </p>
          </div>

          <div>
            <h3 className="font-display text-lg font-semibold mb-4 text-butter">Explore</h3>
            <ul className="space-y-2.5 text-sm">
              <li><Link data-testid="footer-link-about" to="/about" className="text-white/70 hover:text-white transition-colors">About Us</Link></li>
              <li><Link data-testid="footer-link-team" to="/team" className="text-white/70 hover:text-white transition-colors">Our Team</Link></li>
              <li><Link data-testid="footer-link-faq" to="/faq" className="text-white/70 hover:text-white transition-colors">FAQ</Link></li>
              <li><Link data-testid="footer-link-insurance" to="/insurance" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white transition-colors"><ShieldCheck className="w-3.5 h-3.5" />Using Insurance</Link></li>
              <li><Link data-testid="footer-link-testimonials" to="/testimonials" className="text-white/70 hover:text-white transition-colors">Testimonials</Link></li>
              <li><Link data-testid="footer-link-start" to="/start" className="text-white/70 hover:text-white transition-colors">Not sure where to start?</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-display text-lg font-semibold mb-4 text-butter">Services</h3>
            <ul className="space-y-2.5 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    data-testid={`footer-link-service-${s.slug}`}
                    to={`/services/${s.slug}`}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {s.fullName}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-display text-lg font-semibold mb-4 text-butter">Visit Us</h3>
            <ul className="space-y-3 text-sm text-white/70">
              <li>
                <a
                  href={MAPS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-address-link"
                  className="flex items-start gap-2.5 hover:text-white transition-colors"
                >
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-sunny" />{CONTACT_INFO.location}
                </a>
              </li>
              <li className="flex items-start gap-2.5"><Mail className="w-4 h-4 mt-0.5 shrink-0 text-sunny" />{CONTACT_INFO.email}</li>
              <li className="flex items-start gap-2.5"><Phone className="w-4 h-4 mt-0.5 shrink-0 text-sunny" />{CONTACT_INFO.phone}</li>
              <li className="flex items-start gap-2.5"><Clock className="w-4 h-4 mt-0.5 shrink-0 text-sunny" />{CONTACT_INFO.hours}</li>
              <li>
                <a
                  href={WHATSAPP.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("whatsapp_click", { location: "footer" })}
                  data-testid="footer-whatsapp-link"
                  className="flex items-start gap-2.5 hover:text-white transition-colors"
                >
                  <MessageCircle className="w-4 h-4 mt-0.5 shrink-0 text-sunny" />Chat with us on WhatsApp
                </a>
              </li>
              <li>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  className="flex items-start gap-2.5 hover:text-white transition-colors"
                >
                  <Instagram className="w-4 h-4 mt-0.5 shrink-0 text-sunny" />@thrivebahamas
                </a>
              </li>
            </ul>
            <Link
              to="/contact"
              onClick={() => trackEvent("book_now_click", { location: "footer", channel: "contact" })}
              data-testid="footer-book-now-btn"
              className="mt-5 inline-flex items-center gap-2 bg-sunny text-navy font-extrabold text-sm px-5 py-2.5 rounded-full transition-all duration-200 hover:-translate-y-0.5 hover:bg-sunny-dark"
            >
              <CalendarCheck className="w-4 h-4" strokeWidth={2.5} />
              Book Now
            </Link>
          </div>
        </div>

        <div className="border-t border-white/15 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p data-testid="footer-copyright">&copy; {new Date().getFullYear()} Thrive Physiotherapy. All rights reserved. TIN #117479100</p>
          <p className="flex items-center gap-1.5">
            Embodying what it means to thrive <Heart className="w-3.5 h-3.5 text-berry fill-berry" />
          </p>
        </div>
      </div>
    </footer>
  );
};
